import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DocumentDetailsModal.css';

function DocumentDetailsModal({ isOpen, onClose, documentItem, accessToken }) {
  const { t } = useTranslation('database');
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !documentItem?.FileName || !accessToken) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setIsLoading(true);
      setError('');
      try {
        const query = new URLSearchParams({ filename: documentItem.FileName });
        const response = await fetch(`${process.env.REACT_APP_API_URL}/get_site_document_details?${query.toString()}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          signal: controller.signal,
        });
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || t('document_details_error'));
        }
        if (!cancelled) {
          setDetails(payload?.details || {});
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError.message || t('document_details_error'));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isOpen, documentItem, accessToken, t]);

  const metrics = documentItem?.state_metrics || {};
  const detailDoc = details?.document || {};
  const facts = useMemo(() => details?.facts || [], [details]);
  const states = useMemo(() => details?.states || [], [details]);
  const events = useMemo(() => details?.events || [], [details]);
  const legacyStates = useMemo(() => details?.legacy_states || [], [details]);
  const metadata = detailDoc.metadata || {};

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}/${date.getUTCFullYear()}`;
  };

  const formatConfidence = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '-';
    }
    return `${Math.round(Number(value) * 100)}%`;
  };

  const renderSkeletonGrid = (count = 4) => (
    <div className="document-details-grid">
      {Array.from({ length: count }).map((_, index) => (
        <div key={`skeleton-grid-${index}`} className="document-details-skeleton-line" />
      ))}
    </div>
  );

  const renderSkeletonList = (count = 3) => (
    <ul className="document-details-list document-details-list-skeleton">
      {Array.from({ length: count }).map((_, index) => (
        <li key={`skeleton-list-${index}`}>
          <div className="document-details-skeleton-line wide" />
          <div className="document-details-skeleton-line" />
          <div className="document-details-skeleton-line short" />
        </li>
      ))}
    </ul>
  );

  if (!isOpen || !documentItem) return null;

  return (
    <div className="modal-overlay" onClick={(event) => event.target.classList.contains('modal-overlay') && onClose()}>
      <div className="modal-content-details">
        <div className="document-details-header">
          <h3>{t('document_details_title')}</h3>
          <button type="button" className="document-details-close" onClick={onClose}>x</button>
        </div>

        <div className="document-details-subtitle">{documentItem.FileName}</div>

        {isLoading && <p className="document-details-feedback">{t('document_details_loading')}</p>}
        {error && <p className="document-details-feedback error">{error}</p>}

        <div className="document-details-scroll">
          <section className="document-details-section">
            <h4>{t('document_details_general')}</h4>
            {isLoading ? renderSkeletonGrid(4) : (
              <div className="document-details-grid">
              <div><strong>{t('status')}:</strong> {documentItem.Status || '-'}</div>
              <div><strong>{t('document_details_date')}:</strong> {formatDate(documentItem.Date)}</div>
              <div><strong>{t('document_details_type')}:</strong> {documentItem.Type || metadata.type || '-'}</div>
              <div><strong>{t('document_details_meeting_date')}:</strong> {formatDate(detailDoc.meeting_date || metadata.date)}</div>
              </div>
            )}
          </section>

          <section className="document-details-section">
            <h4>{t('document_details_participants')}</h4>
            {isLoading ? renderSkeletonGrid(3) : (
              <div className="document-details-grid">
              <div><strong>EPPGDL:</strong> {metadata.eppgdl || '-'}</div>
              <div><strong>Fiscalizacao:</strong> {metadata.fiscalizacao || '-'}</div>
              <div><strong>Consorcio:</strong> {metadata.consorcio || '-'}</div>
              </div>
            )}
          </section>

          <section className="document-details-section">
            <h4>{t('document_details_processing')}</h4>
            {isLoading ? renderSkeletonGrid(4) : (
              <div className="document-details-grid">
              <div><strong>{t('document_details_extraction_status')}:</strong> {metrics.extraction_status || detailDoc.extraction_status || '-'}</div>
              <div><strong>{t('document_details_chunks')}:</strong> {(metrics.processed_chunks ?? detailDoc.processed_chunks ?? 0)}/{(metrics.eligible_chunks ?? detailDoc.eligible_chunks ?? 0)}</div>
              <div><strong>{t('document_details_chunks_with_events')}:</strong> {metrics.chunks_with_events ?? detailDoc.chunks_with_events ?? 0}</div>
              <div><strong>{t('document_details_updated_at')}:</strong> {formatDate(metrics.updated_at || detailDoc.updated_at)}</div>
              </div>
            )}
          </section>

          <section className="document-details-section">
            <h4>{t('document_details_states')} ({states.length})</h4>
            {isLoading ? renderSkeletonList(2) : states.length === 0 && legacyStates.length === 0 ? (
              <p>{t('document_details_empty_states')}</p>
            ) : (
              <ul className="document-details-list">
                {states.map((state, index) => (
                  <li key={`${state.model_key || state.display_model_key || 'state'}-${index}`}>
                    <div><strong>{state.display_model_key || state.model_key || '-'}</strong></div>
                    <div>{state.estado_raw || '-'}</div>
                    <div>{t('document_details_confidence')}: {formatConfidence(state.confidence_score)} ({state.confidence_label || '-'})</div>
                    {state.decisao ? <div>{t('document_details_decision')}: {state.decisao}</div> : null}
                  </li>
                ))}
                {states.length === 0 && legacyStates.map((state, index) => (
                  <li key={`${state.modelo || 'legacy-state'}-${index}`}>
                    <div>
                      <strong>{state.modelo || '-'}</strong>
                      {' '}
                      <span className="document-details-badge">{t('document_details_legacy')}</span>
                    </div>
                    <div>{state.estado || '-'}</div>
                    {state.decisao ? <div>{t('document_details_decision')}: {state.decisao}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="document-details-section">
            <h4>{t('document_details_facts')} ({facts.length})</h4>
            {isLoading ? renderSkeletonList(3) : facts.length === 0 ? (
              <p>{t('document_details_empty_facts')}</p>
            ) : (
              <ul className="document-details-list">
                {facts.map((fact, index) => (
                  <li key={`${fact.fact_type || 'fact'}-${index}`}>
                    <div><strong>{fact.fact_type || '-'}</strong> - {fact.display_key || fact.entity_key || '-'}</div>
                    <div>{fact.fact_value || '-'}</div>
                    <div>{t('document_details_confidence')}: {formatConfidence(fact.confidence)}</div>
                    {fact.source_quote ? <div className="document-details-quote">"{fact.source_quote}"</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="document-details-section">
            <h4>{t('document_details_events')} ({events.length})</h4>
            {isLoading ? renderSkeletonList(2) : events.length === 0 ? (
              <p>{t('document_details_empty_events')}</p>
            ) : (
              <ul className="document-details-list">
                {events.map((event, index) => (
                  <li key={`${event.model_key || event.canonical_model_key || 'event'}-${index}`}>
                    <div><strong>{event.model_key || event.canonical_model_key || '-'}</strong> - {event.event_type || '-'}</div>
                    <div>{event.estado_raw || '-'}</div>
                    {event.decisao ? <div>{t('document_details_decision')}: {event.decisao}</div> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

export default DocumentDetailsModal;
